<template>
  <v-sheet rounded elevation="1" class="cmd-bar px-4 py-3 mb-3">
    <!-- FT-143: командная шапка клиентов по мотивам CarsCommandBar.
         Решение USER 2026-08-27: без счётчика.
         Раскладка по образцу BookingsCommandBar: зона действий сверху,
         поиск с фильтрами — под разделителем -->
    <!-- zone 1: actions -->
    <div class="d-flex align-center flex-wrap justify-end cmd-bar__actions" style="gap: 8px">
      <v-btn small color="success" :to="{ name: 'NewClient' }" data-testid="clients-add">
        <v-icon small>mdi-plus</v-icon>
        <span class="ml-1">{{ $t("custom.add") }}</span>
      </v-btn>

      <v-btn v-if="showTaxiSync" icon small @click="$emit('sync-taxi')" :loading="taxiSyncLoading">
        <v-icon>mdi-sync</v-icon>
      </v-btn>

      <v-menu offset-y left min-width="210">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
        </template>
        <v-list dense>
          <!-- экспорт как есть: старый GET /clients, всех клиентов, фильтры игнорирует (NS-04) -->
          <v-list-item v-if="isAdminPlus" :disabled="xlsxLoading" @click="$emit('export-xlsx')">
            <v-list-item-icon class="mr-3"><v-icon :color="xlsxLoading ? 'primary' : ''">mdi-file-excel-outline</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t("custom.export_excel") }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="$emit('refresh')">
            <v-list-item-icon class="mr-3"><v-icon>mdi-refresh</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t("custom.refresh") }}</v-list-item-title>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item :href="helpHref" target="_blank">
            <v-list-item-icon class="mr-3"><v-icon>mdi-help-circle</v-icon></v-list-item-icon>
            <v-list-item-title>{{ $t("custom.help") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <v-divider class="cmd-bar__rule my-3"></v-divider>

    <!-- zone 2: search + filters -->
    <div class="d-flex align-center flex-wrap" style="gap: 8px 12px">
      <v-text-field v-model="searchText" :label="$t('custom.search')" prepend-inner-icon="mdi-magnify"
        clearable hide-details dense outlined class="cmd-bar__search" data-testid="clients-search"
        @input="onSearchInput" @click:clear="onSearchClear" />

      <v-menu :close-on-content-click="false" offset-y left transition="slide-y-transition" eager>
        <template v-slot:activator="{ on, attrs }">
          <v-btn small outlined class="cmd-bar__filters" v-bind="attrs" v-on="on" data-testid="clients-filters">
            <v-icon left small>mdi-tune-variant</v-icon>{{ $t("crm.toolbar.filters") }}
            <span v-if="activeCount" class="cmd-bar__fbadge">{{ activeCount }}</span>
          </v-btn>
        </template>
        <clients-filter-panel :value="filters" :sources="sources" :branches="branches"
          @input="$emit('input', $event)" />
      </v-menu>
    </div>

    <!-- zone 3: active filter chips -->
    <div v-if="activeChips.length" class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
      <v-chip v-for="chip in activeChips" :key="chip.key" small close class="cmd-bar__chip"
        @click:close="removeChip(chip.key)">
        <span class="cmd-bar__chipk">{{ chip.label }}:</span>&nbsp;{{ chip.text }}
      </v-chip>
      <v-btn small text class="cmd-bar__clear" @click="$emit('reset')">
        <v-icon left small>mdi-filter-off-outline</v-icon>{{ $t("custom.clear_all") }}
      </v-btn>
    </div>
  </v-sheet>
</template>

<script>
import ClientsFilterPanel from "@/components/dashboard/clients/ClientsFilterPanel.vue";
import { activeFilterChips, withFilterCleared } from "@/components/dashboard/clients/clientsFilterChips";

export default {
  name: "ClientsCommandBar",
  components: { ClientsFilterPanel },
  props: {
    filters: { type: Object, required: true },
    sources: { type: Array, default: () => [] },
    branches: { type: Array, default: () => [] },
    // company_id → текст филиала (branchCity страницы)
    branchText: { type: Function, default: null },
    xlsxLoading: { type: Boolean, default: false },
    showTaxiSync: { type: Boolean, default: false },
    taxiSyncLoading: { type: Boolean, default: false },
  },
  data() {
    return { searchText: (this.filters && this.filters.search) || "", searchTimer: null };
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  computed: {
    helpHref() {
      const locale = this.$root.$i18n.locale;
      return `https://rentprog.${locale === "ru" ? "ru" : "com"}/${locale}/categories/4/guides/14`;
    },
    activeChips() {
      return activeFilterChips(this.filters, {
        t: this.$t.bind(this),
        branchText: this.branchText,
      });
    },
    activeCount() { return this.activeChips.length; },
  },
  watch: {
    "filters.search"(v) {
      if ((v || "") !== this.searchText) this.searchText = v || "";
    },
  },
  methods: {
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const q = (this.searchText || "").trim();
        const next = q.length >= 2 ? q : null;
        if ((this.filters.search || null) === next) return;
        this.$emit("input", { ...this.filters, search: next });
      }, 300);
    },
    onSearchClear() {
      this.searchText = "";
      this.$emit("input", { ...this.filters, search: null });
    },
    removeChip(key) {
      this.$emit("input", withFilterCleared(this.filters, key));
    },
  },
};
</script>

<style scoped>
.cmd-bar__rule {
  opacity: 0.6;
}
.cmd-bar__search {
  flex: 2 1 260px;
  min-width: 200px;
}
/* единая высота строки поиска: dense-поле — 40px, кнопка «Фильтры» под него */
.cmd-bar__filters {
  text-transform: none;
  letter-spacing: 0;
  height: 40px !important;
}
.cmd-bar__fbadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 7px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  background: var(--v-success-base, #4caf50);
  color: #fff;
}
.cmd-bar__chip {
  font-size: 12px;
}
.cmd-bar__chipk {
  opacity: 0.6;
}
.cmd-bar__clear {
  text-transform: none;
  letter-spacing: 0;
}
/* Mobile: как в шапке броней — действия своей строкой, поиск делит строку с «Фильтрами» */
@media (max-width: 599px) {
  .cmd-bar__actions {
    width: 100%;
  }
  .cmd-bar__search {
    flex: 1 1 140px;
    min-width: 0;
  }
}
</style>
